import useBoolean from '@eduzz/houston-hooks/useBoolean';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery, CircularProgress } from '@material-ui/core';
import type { Theme } from '@material-ui/core/styles';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetPlgAnticipation, usePlgSaveAccess } from 'v2/hooks/usePlgAnticipation';

import { ReactComponent as CashCoinIcon } from './assets/CashCoinIcon.svg';
import { ReactComponent as GiftIcon } from './assets/GiftIcon.svg';
import { ReactComponent as RocketTakeoffIcon } from './assets/RocketTakeoffIcon.svg';
import { useStyles } from './styles';

type PlgAnticipationProps = {
  page: string;
};

const PlgAnticipation = ({ page }: PlgAnticipationProps) => {
  const [open, toggleVisibilityModal] = useBoolean(false);
  const [anticipateInProgress, toggleAnticipateProgress] = useBoolean(false);
  const [automaticAnticipateInProgress, toggleAutomaticAnticipateProgress] = useBoolean(false);
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const plgAnticipationQuery = useGetPlgAnticipation();

  const savePlgAccessMutation = usePlgSaveAccess({
    onSuccess: () => {
      plgAnticipationQuery.remove();
    },
    onError: () => {
      toggleAnticipateProgress();
      toggleAutomaticAnticipateProgress();
      toast.error('Ocorreu um erro! Tente novamente.');
    },
  });

  const someCallToActionInProgress = anticipateInProgress || automaticAnticipateInProgress;

  const saveAccess = async () => {
    await savePlgAccessMutation.mutateAsync(page);
    toggleVisibilityModal();
  };

  const handleClose = () => saveAccess();

  const handleAnticipate = async () => {
    await saveAccess();
    history.push('/producer/financial-statement?a=anticipation');
  };

  const handleAutomaticAnticipate = async () => {
    await saveAccess();
    history.push('/producer/financial-statement?a=automatic-anticipation');
  };

  React.useEffect(() => {
    if (!plgAnticipationQuery.data) return;
    const { show, screens } = plgAnticipationQuery.data;
    if (show && screens.includes(page)) toggleVisibilityModal();
  }, [page, plgAnticipationQuery.data, toggleVisibilityModal]);

  return (
    <Dialog
      classes={{
        paper: classes.paper,
      }}
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      fullScreen={isMobile}
      scroll='paper'
      disableEscapeKeyDown
    >
      <DialogTitle>Antecipação de recebíveis</DialogTitle>
      <DialogContent className={classes.content} dividers>
        <div className={classes.contentText} tabIndex={-1}>
          <div className={classes.item}>
            <div className={classes.iconContainer}>
              <RocketTakeoffIcon className={classes.iconSvg} />
            </div>
            <div className={classes.description}>
              <strong>Antecipação de recebíveis</strong>
              <p>
                Acione o poder da antecipação de recebíveis em sua estratégia comercial. Agilize seu fluxo de caixa, conquiste liquidez imediata e
                potencialize seu crescimento no mercado digital. Vamos além juntos!
              </p>
            </div>
          </div>

          <div className={classes.item}>
            <div className={classes.iconContainer}>
              <GiftIcon className={classes.iconSvg} />
            </div>
            <div className={classes.description}>
              <strong>Taxas reduzidas</strong>
              <p>Potencialize seus ganhos com nossas taxas reduzidas. Aumente sua rentabilidade e impulsione sua performance financeira.</p>
            </div>
          </div>

          <div className={classes.item}>
            <div className={classes.iconContainer}>
              <CashCoinIcon className={classes.iconSvg} />
            </div>
            <div className={classes.description}>
              <strong>Disponibilidade imediata de valores</strong>
              <p>
                Não espere para prosperar! Com nossa plataforma, tenha disponibilidade imediata de valores. Agilidade para impulsionar seus projetos e
                conquistar resultados rápidos no mundo do marketing digital.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant='text' onClick={handleClose} disabled={someCallToActionInProgress}>
          Agora não
        </Button>
        <div>
          <Button variant='contained' onClick={handleAutomaticAnticipate} disabled={someCallToActionInProgress}>
            {automaticAnticipateInProgress && <CircularProgress size={12} style={{ marginRight: 4 }} />}
            Ativar antecipação automática
          </Button>
          <Button variant='contained' color='primary' onClick={handleAnticipate} disabled={someCallToActionInProgress}>
            {anticipateInProgress && <CircularProgress size={12} style={{ marginRight: 4 }} color='inherit' />}
            Antecipar meus recebíveis
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default PlgAnticipation;
